import { asFirstName } from "@/domains/string/name";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { IContactModel } from "@/store/contacts/types";
import { AccountProfileImage } from "@/components/layout/components/account-profile/account-profile-image";
import { MDSPanelWidths } from "@/mds/foundations";
import { MDSText, MDSTextColor, MDSTextOverflow, MDSTextVariant } from "@/mds";

export interface ItemListItemLastActionBoxProps {
  lastAction: { contact: IContactModel; verb: "created" | "edited" | "shared"; isMe: boolean };
}

export const ItemListItemLastActionBox = observer(
  ({ lastAction }: ItemListItemLastActionBoxProps) => {
    const { contact, verb, isMe } = lastAction;
    const displayName = contact.profileDisplayName;
    const shortName = isMe ? "You" : asFirstName(displayName);
    const shortText = `${shortName} ${verb}`;
    const fullText = isMe ? `You ${verb}` : `${displayName} ${verb}`;

    return (
      <Wrapper>
        <Container title={fullText}>
          <AvatarSlot>
            <AccountProfileImage
              size={16}
              photoUrl={contact.profilePhotoUrl}
              displayName={contact.profileDisplayName}
            />
          </AvatarSlot>
          <TextSlot>
            <MDSText
              variant={MDSTextVariant.TextXs}
              color={MDSTextColor.Secondary}
              overflow={MDSTextOverflow.Ellipsis}
              numberOfLines={1}
            >
              {shortText}
            </MDSText>
          </TextSlot>
        </Container>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  gap: ${({ theme }) => theme.spacing["2"]};
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const AvatarSlot = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const TextSlot = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
  min-width: 0;

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    display: none;
    width: 0;
  }
`;
